import { useNavigate } from 'react-router-dom';
import {
  MAX_TEAMS_PER_OPERATOR,
  canJoinAnotherTeam,
  hasCreatedOwnTeam,
  isUserInTeam,
  type SystemUserInfo,
} from '@/api';

interface NoTeamPromptProps {
  profile?: SystemUserInfo | null;
}

export default function NoTeamPrompt({ profile = null }: NoTeamPromptProps) {
  const navigate = useNavigate();
  const inTeam = isUserInTeam(profile);
  const createUsed = hasCreatedOwnTeam(profile);
  const canJoin = canJoinAnotherTeam(profile);
  const canCreate = !createUsed && canJoin;
  const atLimit = !canJoin;

  return (
    <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
      <div className="h-0.5 w-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-400" />
      <div className="p-3 text-center space-y-2.5">
        <h2 className="text-amber-900 font-semibold text-sm">
          {inTeam ? 'Join or create a team' : 'No Team Yet'}
        </h2>
        <p className="text-amber-800 font-medium text-xs">
          {inTeam ? 'You can still add another team' : 'Get started with a team'}
        </p>
        <p className="text-left text-amber-800/70 text-[11px] leading-relaxed">
          Every account can join up to {MAX_TEAMS_PER_OPERATOR} teams, and may create one team of
          their own.
        </p>
        {atLimit && (
          <p className="text-amber-800 text-[10px] bg-amber-100 border border-amber-200 rounded-md px-2 py-1.5">
            You have reached the limit of {MAX_TEAMS_PER_OPERATOR} teams.
          </p>
        )}
        {createUsed && !atLimit && (
          <p className="text-amber-700/80 text-[10px]">
            You have already created your own team. You can still join another team.
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            disabled={!canCreate}
            onClick={() => navigate('/team/create')}
            className="w-full h-8 rounded-md bg-amber-500 text-white font-medium text-xs cursor-pointer hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
          >
            <i className="ri-add-circle-line text-sm" />
            Create Team
          </button>
          <button
            type="button"
            disabled={!canJoin}
            onClick={() => navigate('/me/team/join')}
            className="mt-2 w-full h-8 rounded-md border border-amber-500 text-amber-700 font-medium text-xs cursor-pointer hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
          >
            <i className="ri-login-circle-line text-sm" />
            Join Team
          </button>
        </div>
      </div>
    </div>
  );
}
